import Head from 'next/head';
import React, { ReactNode, useMemo } from 'react';

import { MetaTagProps } from '@tb-core/adapters/contentful/graphql/webpage-adapter';
import { dataDogInitialize } from '@tb-core/helpers/analytics/data-dog';
import usePageContext from '@tb-core/hooks/use-page-context';

interface DocumentHeadProps {
    children?: ReactNode;
    title?: string;
}

const DocumentHeadRegion = ({ children, title }: DocumentHeadProps) => {
    const {
        content: { pageData },
        publicEnv: { appleItunesId, assetPath, dataDogEnv, googlePlayId }
    } = usePageContext();
    const {
        metaTagsCollection,
        seoCanonicalUrl,
        seoDescription,
        seoTitle,
        topicMeta
    } = pageData;

    // Use NPM for Data Dog for init and logs
    if (dataDogEnv) {
        dataDogInitialize(dataDogEnv);
    }

    return useMemo(() => {
        return (
            <Head>
                {/* SEO specific elements */}
                <title>{title ? title : seoTitle}</title>
                <meta
                    content={seoDescription}
                    key="seoDescription"
                    name="description"
                />
                <link
                    href={seoCanonicalUrl}
                    key="seoCanonical"
                    rel="canonical"
                />
                {/* End of SEO specific elements */}
                {appleItunesId && (
                    <meta
                        name="apple-itunes-app"
                        content={`app-id=${appleItunesId}`}
                    />
                )}
                {googlePlayId && (
                    <meta
                        name="google-play-app"
                        content={`app-id=${googlePlayId}`}
                    />
                )}
                {metaTagsCollection &&
                    metaTagsCollection?.items.map(
                        (metaTag: MetaTagProps, i: number) => (
                            <meta
                                content={metaTag.description}
                                key={i}
                                name={metaTag.name}
                            />
                        )
                    )}
                <link
                    rel="shortcut icon"
                    href={`${assetPath}/_static/icons/favicon.ico`}
                />
                <link
                    href={`${assetPath}/_static/styles/global.css`}
                    rel="stylesheet"
                />

                {children}
            </Head>
        );
    }, [
        appleItunesId,
        assetPath,
        googlePlayId,
        seoCanonicalUrl,
        seoDescription,
        seoTitle,
        title,
        topicMeta
    ]);
};

export default DocumentHeadRegion;
